import { sortByName, sortFeatureValues, sortKeysAlphabetically } from '../../../../../utils/sortingUtils';

export function FeatureTree({
    featuresTree,
    featureSearchQuery,
    setFeatureSearchQuery,
    expandedFolders,
    expandedFeatures,
    selectedFeatureId,
    selectedValueId,
    newFeatureInlineName,
    setNewFeatureInlineName,
    inlineNewValTexts,
    setInlineNewValTexts,
    isAddingFeatureInline,
    onToggleFolder,
    onToggleFeature,
    onSelectFeature,
    onAddFeatureInline,
    onAddValueInline,
    onReorderValues
}) {
    const renderTreeNode = (node, path = '', depth = 0) => {
        const childFolders = sortKeysAlphabetically(node.children || {});
        const childFeatures = sortByName(node.features || [], 'name');

        return (
            <div key={path || 'root'} style={{ marginLeft: depth > 0 ? '0.75rem' : 0 }}>
                {/* Subfolders */}
                {childFolders.map(name => {
                    const folderPath = path ? `${path}.${name}` : name;
                    const isExpanded = expandedFolders[folderPath] !== false; // default expanded
                    return (
                        <div key={folderPath} style={{ marginTop: '0.25rem' }}>
                            <div 
                                onClick={() => onToggleFolder(folderPath)} 
                                style={{
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.4rem', 
                                    cursor: 'pointer', 
                                    fontWeight: '600', 
                                    color: 'var(--text-primary)',
                                    padding: '0.2rem 0',
                                    fontSize: '0.8rem'
                                }}
                            >
                                <i className={`fa-solid ${isExpanded ? 'fa-caret-down' : 'fa-caret-right'}`} style={{ width: '10px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}></i>
                                <i className={`fa-regular ${isExpanded ? 'fa-folder-open' : 'fa-folder'}`} style={{ color: 'var(--accent)', fontSize: '0.85rem' }}></i>
                                <span>{name}</span>
                            </div>
                            {isExpanded && (
                                <div style={{ borderLeft: '1px solid var(--border)', marginLeft: '6px', paddingLeft: '6px' }}>
                                    {renderTreeNode(node.children[name], folderPath, depth + 1)}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Leaf features */}
                {childFeatures.map(feat => {
                    const isSelected = selectedFeatureId === feat.id;
                    const labelText = feat.label ? ` (${feat.label})` : '';
                    const hasValues = Array.isArray(feat.raw.values) && feat.raw.values.length > 0;
                    const isExpanded = !!expandedFeatures[feat.id];
                    return (
                        <div key={feat.id} style={{ marginTop: '0.15rem' }}>
                            <div
                                onClick={() => onSelectFeature(feat.raw)}
                                onKeyDown={(event) => {
                                    if (event.target !== event.currentTarget) return;
                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                    event.preventDefault();
                                    onSelectFeature(feat.raw);
                                }}
                                tabIndex={0}
                                role="button"
                                aria-pressed={isSelected}
                                className={`item-highlight clickable-row${isSelected ? ' is-selected' : ''}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.8rem',
                                    width: '100%'
                                }}
                            >
                                {hasValues ? (
                                    <span 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleFeature(feat.id);
                                        }}
                                        style={{
                                            width: '12px', 
                                            display: 'inline-flex', 
                                            justifyContent: 'center', 
                                            alignItems: 'center',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <i className={`fa-solid ${isExpanded ? 'fa-caret-down' : 'fa-caret-right'}`} style={{ fontSize: '0.75rem' }}></i>
                                    </span>
                                ) : (
                                    <span style={{ width: '12px' }} />
                                )}
                                <i className="fa-solid fa-cube" style={{ fontSize: '0.75rem' }}></i>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {feat.name}
                                    <span className="row-muted" style={{ fontStyle: 'italic' }}>{labelText}</span>
                                </span>
                            </div>

                            {(isExpanded || !hasValues) && (
                                <div style={{ borderLeft: '1px dotted var(--border)', marginLeft: '1.25rem', paddingLeft: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.15rem' }}>
                                    {Array.isArray(feat.raw.values) && sortFeatureValues(feat.raw.values).map(val => {
                                        const isValueSelected = selectedValueId === val.id;
                                        const displayValue = val.value !== undefined && val.value !== null ? String(val.value) : '';
                                        return (
                                            <div
                                                key={val.id}
                                                draggable={Boolean(onReorderValues)}
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData('text/value-id', val.id);
                                                    e.dataTransfer.setData('text/feature-id', feat.id);
                                                }}
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    const draggedId = e.dataTransfer.getData('text/value-id');
                                                    const featureId = e.dataTransfer.getData('text/feature-id');
                                                    if (String(featureId) === String(feat.id) && draggedId && String(draggedId) !== String(val.id)) {
                                                        onReorderValues?.(feat.id, draggedId, val.id);
                                                    }
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectFeature(feat.raw, val.id);
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.target !== event.currentTarget) return;
                                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                                    event.preventDefault();
                                                    onSelectFeature(feat.raw, val.id);
                                                }}
                                                tabIndex={0}
                                                role="button"
                                                aria-pressed={isValueSelected}
                                                className={`item-highlight clickable-row${isValueSelected ? ' is-selected' : ''}`}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.35rem',
                                                    padding: '0.15rem 0.25rem',
                                                    fontSize: '0.75rem',
                                                    width: '100%'
                                                }}
                                            >
                                                <i className="fa-solid fa-circle" style={{ fontSize: '0.25rem' }}></i>
                                                <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{displayValue}</span>
                                            </div>
                                        );
                                    })}

                                    {/* Inline Add Value Form inside the Tree View */}
                                    <form 
                                        onSubmit={(e) => onAddValueInline(e, feat.id)} 
                                        style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem', alignItems: 'center' }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <input 
                                            className="input-field" 
                                            placeholder="Add value..." 
                                            value={inlineNewValTexts[feat.id] || ''} 
                                            onChange={e => setInlineNewValTexts(prev => ({ ...prev, [feat.id]: e.target.value }))}
                                            style={{ fontSize: '0.75rem', padding: '2px 4px', width: '90px', margin: 0, height: '24px' }}
                                        />
                                        <button 
                                            type="submit" 
                                            className="btn-secondary" 
                                            disabled={!(inlineNewValTexts[feat.id] || '').trim()}
                                            style={{ padding: '2px 6px', fontSize: '0.7rem', height: '24px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            Add
                                        </button>
                                    </form>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div style={{ width: '250px', minWidth: '250px', display: 'flex', flexDirection: 'column', gap: '0.75rem', borderRight: '1px solid var(--border)', paddingRight: '1.25rem', overflow: 'hidden' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.75rem' }}></i>
                <input 
                    type="text" 
                    placeholder="Search feature..." 
                    className="input-field" 
                    value={featureSearchQuery}
                    onChange={e => setFeatureSearchQuery(e.target.value)}
                    style={{ margin: 0, padding: '0.3rem 0.4rem 0.3rem 1.5rem', fontSize: '0.75rem' }}
                />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
                {renderTreeNode(featuresTree)}

                {/* Inline Add Feature Form at the bottom of the Tree */}
                <form 
                    onSubmit={onAddFeatureInline} 
                    style={{ display: 'flex', gap: '0.25rem', marginTop: '0.75rem', marginLeft: '0.5rem', alignItems: 'center' }}
                >
                    <input 
                        className="input-field" 
                        placeholder="Add feature..." 
                        value={newFeatureInlineName} 
                        onChange={e => setNewFeatureInlineName(e.target.value)} 
                        style={{ fontSize: '0.75rem', padding: '2px 4px', width: '110px', margin: 0, height: '24px' }}
                    />
                    <button 
                        type="submit" 
                        className="btn-secondary" 
                        disabled={isAddingFeatureInline || !newFeatureInlineName.trim()}
                        style={{ padding: '2px 6px', fontSize: '0.7rem', height: '24px', display: 'inline-flex', alignItems: 'center' }}
                    >
                        {isAddingFeatureInline ? 'Adding...' : 'Add'}
                    </button>
                </form>
            </div>
        </div>
    );
}
